import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Text, Flex } from '@tonic-ui/react';
import Login from '../Login/login';
import { isGovCloudRegion } from '../../utils/helpers';
import getQueryParams from '../../utils/query-params';
import { ButtonRouteLink } from '../../components/route-link';
import heroBannerPng from '../../assets/landing/img/hero_banner.png';
import govCloudBanner from '../../assets/landing/img/govCloudBanner.png';

interface HeroBannerProps {
  showAlertBanner?: (arg: Record<string, string | boolean>) => void;
}

const { event = '' } = getQueryParams(window.location);
const isMarketplaceUser =
  event === 'AWSMarketplaceSubscription' ||
  event === 'TrendMicroSubscription' ||
  event === 'AzureMarketplaceSubscription';

export const CTAButtonRouteLink = () => {
  const { t } = useTranslation();
  return (
    !isGovCloudRegion && (
      <ButtonRouteLink
        variant="secondary"
        data-id={isMarketplaceUser ? 'signUp-for-cloudone-button' : 'get-started-for-free-button'}
        changeApp={false}
        href={isMarketplaceUser ? '/register' : '/trial'}
        _hover={{
          color: 'white',
          borderColor: 'white',
          backgroundColor: 'rgba(255,255,255,0.25)',
        }}
        _focus={{ color: 'white', borderColor: 'white' }}
        _active={{ color: 'white', borderColor: 'white' }}
        fontFamily="interstate"
        fontWeight={400}
        padding="6px 12px"
        color="white"
      >
        <Text>
          {isMarketplaceUser
            ? t('publicLandingPage.heroBanner.signUpCloudOneBtnTxt')
            : t('publicLandingPage.heroBanner.getStartedBtnTxt')}
        </Text>
      </ButtonRouteLink>
    )
  );
};

CTAButtonRouteLink.displayName = 'CTAButtonRouteLink';

const HeroBanner = ({ showAlertBanner }: HeroBannerProps): JSX.Element => {
  const { t } = useTranslation();
  const banner = isGovCloudRegion ? govCloudBanner : heroBannerPng;

  return (
    <Box position="relative">
      {/*Create a custom clip path used for a dynamic clipping effect on background. Creates a curved bottom that is the same for all viewport widths. */}
      <svg width="0" height="0">
        <defs>
          <clipPath id="clip-shape-md" clipPathUnits="objectBoundingBox">
            <path d="M0,0.914 V0 H1 V0.841 C0.919,0.885,0.76,0.939,0.6,0.972 C0.395,1,0.102,0.972,0,0.914" />
          </clipPath>
          <clipPath id="clip-shape-sm" clipPathUnits="objectBoundingBox">
            <path d="M0,0.914 V0 H1 V0.881 C0.919,0.915,0.76,0.944,0.6,0.952 C0.395,.952,0.102,0.952,0,0.924" />
          </clipPath>
        </defs>
      </svg>
      <Box
        position="absolute"
        top={-2}
        left={0}
        right={0}
        bottom="0"
        clipPath={{ sm: 'url(#clip-shape-sm)', md: 'url(#clip-shape-md)' }}
        background={`url(${banner}) no-repeat bottom left`}
        backgroundSize="cover"
      />
      <Flex justifyContent="center" paddingX={{ sm: '10%', md: '48px', lg: '120px' }}>
        <Flex
          position="relative"
          data-id="hero-banner"
          direction={{ sm: 'column', md: 'row' }}
          gap="48px"
          justifyContent="space-between"
          maxWidth="1440px"
          width="100%"
        >
          <Stack
            direction="column"
            flex="1 4 auto"
            spacing="4x"
            paddingTop={isGovCloudRegion ? { md: '40px', lg: '70px' } : { md: '66px', lg: '128px' }}
            fontFamily="opensans"
          >
            <Text
              as="h1"
              fontWeight="700"
              fontSize={isGovCloudRegion ? { sm: '34px', md: '34px', lg: '40px' } : '56px'}
              width={isGovCloudRegion && { md: '300px', lg: '364px' }}
              color="white"
              lineHeight={isGovCloudRegion ? '52px' : '67px'}
              margin={isGovCloudRegion ? { md: '0', lg: '0 0 30px' } : '0'}
            >
              {isGovCloudRegion ? t('govTrendCloudOne') : t('trendCloudOne')}
            </Text>
            <Text
              fontWeight="400"
              fontSize={isGovCloudRegion ? '16px' : '18px'}
              lineHeight="23px"
              color="white"
              maxWidth={isGovCloudRegion ? '635px' : '690px'}
            >
              {isGovCloudRegion
                ? t('publicLandingPage.heroBanner.govDescription')
                : t('publicLandingPage.heroBanner.description')}
            </Text>
            <Box>
              <CTAButtonRouteLink />
            </Box>
          </Stack>

          {/* Login form*/}
          {/* bottom padding is to prevent overlap with clipped bottom of BG image */}
          <Box
            paddingTop={{ md: '24px', lg: '66px' }}
            paddingBottom={{ sm: '100px', md: '84px' }}
            flex="0 1 383px"
            maxWidth="384px"
          >
            <Login hideHeading={true} showAlertBanner={showAlertBanner} />
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

HeroBanner.displayName = 'HeroBanner';

export default HeroBanner;
